import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Modal from 'react-modal';
import { toast } from 'react-toastify';
import { getApi } from '../../../Services/apiService';

const Subscription = () => {
    const navigate = useNavigate();
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [isEditModalOpen, setIsEditModalOpen] = useState(false);
    const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
    const [isViewModalOpen, setIsViewModalOpen] = useState(false);
    const [editingSubscriptionId, setEditingSubscriptionId] = useState(null);
    const [deletingSubscriptionId, setDeletingSubscriptionId] = useState(null);
    const [viewingSubscriptionId, setViewingSubscriptionId] = useState(null);
    const [viewSubscriptionData, setViewSubscriptionData] = useState(null);
    const [viewLoader, setViewLoader] = useState(false);
    const [deleteLoader, setDeleteLoader] = useState(false);
    const [formData, setFormData] = useState({
        planName: '',
        price: '',
        duration: '',
        features: '',
        isActive: true
    });
    const [formErrors, setFormErrors] = useState({});
    const [loader, setLoader] = useState(false);
    
    // API state
    const [subscriptions, setSubscriptions] = useState([]);
    const [loading, setLoading] = useState(false);
    const [page, setPage] = useState(1);
    const [limit] = useState(10);
    const [search, setSearch] = useState('');
    const [totalSubscriptions, setTotalSubscriptions] = useState(0);
    const [pagination, setPagination] = useState({
        page: 1,
        take: 10,
        totalItems: 0,
        totalPages: 0,
        hasNextPage: false,
        hasPreviousPage: false
    });

    const handleView = async (id) => {
        setViewingSubscriptionId(id);
        setIsViewModalOpen(true);
        setViewSubscriptionData(null);
        setViewLoader(true);
        try {
            const response = await getApi(`/api/v1/admin/subscriptions/${id}`);
            const raw = response?.data ?? response;
            if (!raw) {
                toast.error('Subscription details not found.');
                closeViewModal();
                return;
            }
            const d = raw;
            const planLabel = d.subscriptionPlan ?? d.plan ?? d.planName;
            const isPremium = /yearly|monthly|premium/i.test(planLabel || '');
            setViewSubscriptionData({
                name: d.name ?? '—',
                email: d.email ?? '—',
                plan: planLabel ?? 'Free',
                planKey: d.planKey ?? (isPremium ? 'premium' : 'free'),
                status: d.subscriptionStatus ?? d.status ?? 'Active',
                amount: d.amount ?? '—',
                paymentMethod: d.paymentMethod != null && d.paymentMethod !== '' ? d.paymentMethod : (d.cardLast4 ? `Visa •••• ${d.cardLast4}` : '—'),
                startDate: d.startDate ?? '—',
                endDate: d.endDate ?? '—',
                nextBilling: d.nextBillingDate ?? d.nextBilling ?? '—',
                transactionId: d.transactionId ?? '—',
                renewalStatus: d.renewalStatus ?? (d.subscriptionStatus === 'Active' ? 'Auto-Renew' : '—')
            });
        } catch (error) {
            console.error('Error fetching subscription details:', error);
            toast.error('Failed to load subscription details.');
            closeViewModal();
        } finally {
            setViewLoader(false);
        }
    };

    const closeViewModal = () => {
        setIsViewModalOpen(false);
        setViewingSubscriptionId(null);
        setViewSubscriptionData(null);
    };

    const handleEdit = (id) => {
        const subscription = subscriptions.find(s => s.id === id);
        if (subscription) {
            setEditingSubscriptionId(id);
            setFormData({
                planName: subscription.plan || '',
                price: subscription.amount?.replace(/[^0-9.]/g, '') || '',
                duration: subscription.plan || '',
                features: subscription.features || '',
                isActive: subscription.isActive !== undefined ? subscription.isActive : true
            });
            setFormErrors({});
            setIsEditModalOpen(true);
        }
    };

    const handleDelete = (id) => {
        setDeletingSubscriptionId(id);
        setIsDeleteModalOpen(true);
    };

    const closeDeleteModal = () => {
        setIsDeleteModalOpen(false);
        setDeletingSubscriptionId(null);
    };

    const confirmDelete = async () => {
        if (!deletingSubscriptionId) return;
        
        setDeleteLoader(true);
        try {
            // TODO: Implement delete subscription API call
            toast.info('Delete functionality coming soon');
            closeDeleteModal();
        } catch (error) {
            console.error('Error deleting subscription:', error);
            toast.error('Failed to delete subscription. Please try again.');
        } finally {
            setDeleteLoader(false);
        }
    };

    // Fetch subscriptions from API (supports ?page= & limit= or take=; optional data.pagination)
    const fetchSubscriptions = async () => {
        setLoading(true);
        try {
            const params = new URLSearchParams();
            params.set('page', String(page));
            params.set('limit', String(limit));
            const response = await getApi(`/api/v1/admin/subscriptions?${params.toString()}`);
            const data = response?.data ?? {};
            const list = Array.isArray(data?.list) ? data.list : (Array.isArray(data?.subscriptions) ? data.subscriptions : []);
            const total = data?.total ?? list.length;

            const normalized = list.map((item) => ({
                id: item.id,
                srNo: item.srNo,
                name: item.name ?? '—',
                plan: item.plan ?? 'Free',
                planKey: item.planKey ?? 'free',
                amount: item.amount ?? '—',
                aiUsageCount: item.aiUsageCount ?? 0,
                startDate: item.startDate ?? '—',
                endDate: item.endDate ?? '—',
                transactionId: item.transactionId ?? '—',
                status: item.status ?? 'Active',
                isActive: item.status === 'Active'
            }));
            setSubscriptions(normalized);
            setTotalSubscriptions(total);

            const pag = data?.pagination;
            if (pag) {
                setPagination({
                    page: pag.page ?? page,
                    take: pag.take ?? pag.limit ?? limit,
                    totalItems: pag.totalItems ?? total,
                    totalPages: pag.totalPages ?? (Math.ceil(total / limit) || 1),
                    hasNextPage: Boolean(pag.hasNextPage),
                    hasPreviousPage: Boolean(pag.hasPreviousPage)
                });
            } else {
                const totalPages = Math.ceil(total / limit) || 1;
                setPagination({
                    page,
                    take: limit,
                    totalItems: total,
                    totalPages,
                    hasNextPage: page < totalPages,
                    hasPreviousPage: page > 1
                });
            }
        } catch (error) {
            console.error('Error fetching subscriptions:', error);
            setSubscriptions([]);
            setTotalSubscriptions(0);
            setPagination({ page: 1, take: limit, totalItems: 0, totalPages: 0, hasNextPage: false, hasPreviousPage: false });
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchSubscriptions();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [page, search]);

    const handleAddSubscription = () => {
        setIsModalOpen(true);
    };

    const handleExport = () => {
        toast.info('Export functionality coming soon');
    };

    const closeModal = () => {
        setIsModalOpen(false);
        setIsEditModalOpen(false);
        setEditingSubscriptionId(null);
        setFormData({
            planName: '',
            price: '',
            duration: '',
            features: '',
            isActive: true
        });
        setFormErrors({});
    };

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));
        
        if (formErrors[name]) {
            setFormErrors(prev => ({
                ...prev,
                [name]: ''
            }));
        }
    };

    const validateForm = () => {
        const errors = {};
        
        if (!formData.planName.trim()) {
            errors.planName = 'Plan name is required';
        }
        
        if (!formData.price.trim()) {
            errors.price = 'Price is required';
        }
        
        if (!formData.duration.trim()) {
            errors.duration = 'Duration is required';
        }
        
        setFormErrors(errors);
        return Object.keys(errors).length === 0;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        
        if (!validateForm()) {
            return;
        }
        
        setLoader(true);
        try {
            // TODO: Implement add subscription API call
            toast.info('Add subscription functionality coming soon');
            closeModal();
            fetchSubscriptions();
        } catch (error) {
            console.error('Error adding subscription:', error);
            toast.error('Failed to add subscription. Please try again.');
        } finally {
            setLoader(false);
        }
    };

    const handleUpdateSubmit = async (e) => {
        e.preventDefault();
        
        if (!validateForm()) {
            return;
        }
        
        setLoader(true);
        try {
            // TODO: Implement update subscription API call
            toast.info('Update subscription functionality coming soon');
            closeModal();
            fetchSubscriptions();
        } catch (error) {
            console.error('Error updating subscription:', error);
            toast.error('Failed to update subscription. Please try again.');
        } finally {
            setLoader(false);
        }
    };

    return (
        <div className='RightSideDiv subscription-page'>
            {/* All Subscriptions Section */}
            <div className="shelters-header">
                <h2>Users</h2>
            </div>

            <div className="shelters-section">
                <div className="table-outer">
                    {loading ? (
                        <div style={{ padding: '40px', textAlign: 'center' }}>
                            <p>Loading subscriptions...</p>
                        </div>
                    ) : subscriptions.length === 0 ? (
                        <div style={{ padding: '40px', textAlign: 'center' }}>
                            <p>No subscriptions found.</p>
                        </div>
                    ) : (
                        <div className="table-wrapper">
                            <table className="table subscription-table">
                                <thead>
                                    <tr className="t-heading">
                                        <th><h5>Sr.no.</h5></th>
                                        <th><h5>Name</h5></th>
                                        <th><h5>Plan</h5></th>
                                        <th><h5>Amount</h5></th>
                                        <th><h5>AI Usage Count</h5></th>
                                        <th><h5>Start Date</h5></th>
                                        <th><h5>End Date</h5></th>
                                        <th><h5>Transaction ID</h5></th>
                                        <th><h5>Status</h5></th>
                                        <th><h5>Action</h5></th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {subscriptions.map((subscription, index) => (
                                        <tr key={subscription.id}>
                                            <td><h5>{subscription.srNo ?? index + 1}</h5></td>
                                            <td><h5>{subscription.name}</h5></td>
                                            <td>
                                                <span className={`plan-tag ${subscription.planKey === 'premium' ? 'premium' : 'free'}`}>
                                                    {subscription.plan}
                                                </span>
                                            </td>
                                            <td><h5>{subscription.amount}</h5></td>
                                            <td><h5>{subscription.aiUsageCount}</h5></td>
                                            <td><h5>{subscription.startDate}</h5></td>
                                            <td><h5>{subscription.endDate}</h5></td>
                                            <td><h5>{subscription.transactionId}</h5></td>
                                            <td>
                                                <span className={`status-badge ${subscription.status === 'Active' ? 'active' : 'inactive'}`}>
                                                    {subscription.status}
                                                </span>
                                            </td>
                                            <td className="action">
                                                <button 
                                                    className="view-detail-btn" 
                                                    onClick={() => handleView(subscription.id)}
                                                    title="View Detail"
                                                >
                                                    View Detail
                                                </button>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}
                </div>
                {!loading && pagination.totalPages > 0 && (
                    <div className="pagination-bar">
                        <div className="pagination-controls">
                            <button type="button" className="pagination-btn pagination-prev-next" disabled={!pagination.hasPreviousPage} onClick={() => setPage((p) => Math.max(1, p - 1))} aria-label="Previous page">
                                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
                                    <path d="M15 18L9 12L15 6" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
                                </svg>
                            </button>
                            <div className="pagination-pages">
                                {(() => {
                                    const { page: currentPage, totalPages } = pagination;
                                    const pages = [];
                                    const showEllipsisStart = totalPages > 5 && currentPage > 3;
                                    const showEllipsisEnd = totalPages > 5 && currentPage < totalPages - 2;
                                    let start = 1, end = totalPages;
                                    if (totalPages > 5) {
                                        if (currentPage <= 3) { start = 1; end = 4; } else if (currentPage >= totalPages - 2) { start = totalPages - 3; end = totalPages; } else { start = currentPage - 1; end = currentPage + 1; }
                                    }
                                    if (showEllipsisStart) pages.push(1, 'ellipsis-start');
                                    for (let i = start; i <= end; i++) pages.push(i);
                                    if (showEllipsisEnd && end < totalPages) pages.push('ellipsis-end', totalPages);
                                    return pages.map((item, idx) =>
                                        item === 'ellipsis-start' || item === 'ellipsis-end' ? <span key={`e-${idx}`} className="pagination-ellipsis">…</span> : (
                                            <button key={item} type="button" className={`pagination-btn pagination-page ${item === currentPage ? 'active' : ''}`} onClick={() => setPage(item)} aria-label={`Page ${item}`} aria-current={item === currentPage ? 'page' : undefined}>{item}</button>
                                        )
                                    );
                                })()}
                            </div>
                            <button type="button" className="pagination-btn pagination-prev-next" disabled={!pagination.hasNextPage} onClick={() => setPage((p) => p + 1)} aria-label="Next page">
                                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
                                    <path d="M9 18L15 12L9 6" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
                                </svg>
                            </button>
                        </div>
                    </div>
                )}
            </div>

            {/* Add Subscription Modal */}
            <Modal
                isOpen={isModalOpen}
                onRequestClose={closeModal}
                contentLabel="Add Subscription Modal"
                className="add-shelter-modal"
                overlayClassName="add-shelter-overlay"
                ariaHideApp={false}
            >
                <div className="modal-header">
                    <div>
                        <h2>Add Subscription</h2>
                        <p>Subscription plan information</p>
                    </div>
                    <button className="close-btn" onClick={closeModal}>
                        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <path d="M18 6L6 18M6 6L18 18" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                        </svg>
                    </button>
                </div>

                <form onSubmit={handleSubmit}>
                    <div className="form-content">
                        <div className="form-group">
                            <label htmlFor="planName">Plan Name <span className="required">*</span></label>
                            <input
                                type="text"
                                id="planName"
                                name="planName"
                                value={formData.planName}
                                onChange={handleInputChange}
                                placeholder="Enter plan name"
                                className={formErrors.planName ? 'error' : ''}
                            />
                            {formErrors.planName && <span className="error-message">{formErrors.planName}</span>}
                        </div>

                        <div className="form-row">
                            <div className="form-group form-group-half">
                                <label htmlFor="price">Price <span className="required">*</span></label>
                                <input
                                    type="number"
                                    id="price"
                                    name="price"
                                    value={formData.price}
                                    onChange={handleInputChange}
                                    placeholder="Enter price"
                                    className={formErrors.price ? 'error' : ''}
                                />
                                {formErrors.price && <span className="error-message">{formErrors.price}</span>}
                            </div>

                            <div className="form-group form-group-half">
                                <label htmlFor="duration">Duration <span className="required">*</span></label>
                                <input
                                    type="text"
                                    id="duration"
                                    name="duration"
                                    value={formData.duration}
                                    onChange={handleInputChange}
                                    placeholder="e.g., 1 month, 1 year"
                                    className={formErrors.duration ? 'error' : ''}
                                />
                                {formErrors.duration && <span className="error-message">{formErrors.duration}</span>}
                            </div>
                        </div>

                        <div className="form-group">
                            <label htmlFor="features">Features</label>
                            <textarea
                                id="features"
                                name="features"
                                value={formData.features}
                                onChange={handleInputChange}
                                placeholder="Enter features"
                                rows="4"
                                className={formErrors.features ? 'error' : ''}
                            />
                            {formErrors.features && <span className="error-message">{formErrors.features}</span>}
                        </div>
                    </div>

                    <div className="modal-footer">
                        <button type="submit" className="submit-btn" disabled={loader}>
                            {loader ? 'Adding...' : 'Add Subscription'}
                        </button>
                    </div>
                </form>
            </Modal>

            {/* Edit Subscription Modal */}
            <Modal
                isOpen={isEditModalOpen}
                onRequestClose={closeModal}
                contentLabel="Edit Subscription Modal"
                className="add-shelter-modal"
                overlayClassName="add-shelter-overlay"
                ariaHideApp={false}
            >
                <div className="modal-header">
                    <div>
                        <h2>Edit Subscription</h2>
                        <p>Subscription plan information</p>
                    </div>
                    <button className="close-btn" onClick={closeModal}>
                        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <path d="M18 6L6 18M6 6L18 18" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                        </svg>
                    </button>
                </div>

                <form onSubmit={handleUpdateSubmit}>
                    <div className="form-content">
                        <div className="form-group">
                            <label htmlFor="edit-planName">Plan Name <span className="required">*</span></label>
                            <input
                                type="text"
                                id="edit-planName"
                                name="planName"
                                value={formData.planName}
                                onChange={handleInputChange}
                                placeholder="Enter plan name"
                                className={formErrors.planName ? 'error' : ''}
                            />
                            {formErrors.planName && <span className="error-message">{formErrors.planName}</span>}
                        </div>

                        <div className="form-row">
                            <div className="form-group form-group-half">
                                <label htmlFor="edit-price">Price <span className="required">*</span></label>
                                <input
                                    type="number"
                                    id="edit-price"
                                    name="price"
                                    value={formData.price}
                                    onChange={handleInputChange}
                                    placeholder="Enter price"
                                    className={formErrors.price ? 'error' : ''}
                                />
                                {formErrors.price && <span className="error-message">{formErrors.price}</span>}
                            </div>

                            <div className="form-group form-group-half">
                                <label htmlFor="edit-duration">Duration <span className="required">*</span></label>
                                <input
                                    type="text"
                                    id="edit-duration"
                                    name="duration"
                                    value={formData.duration}
                                    onChange={handleInputChange}
                                    placeholder="e.g., 1 month, 1 year"
                                    className={formErrors.duration ? 'error' : ''}
                                />
                                {formErrors.duration && <span className="error-message">{formErrors.duration}</span>}
                            </div>
                        </div>

                        <div className="form-group">
                            <label htmlFor="edit-features">Features</label>
                            <textarea
                                id="edit-features"
                                name="features"
                                value={formData.features}
                                onChange={handleInputChange}
                                placeholder="Enter features"
                                rows="4"
                                className={formErrors.features ? 'error' : ''}
                            />
                            {formErrors.features && <span className="error-message">{formErrors.features}</span>}
                        </div>
                    </div>

                    <div className="modal-footer">
                        <button type="submit" className="submit-btn" disabled={loader}>
                            {loader ? 'Updating...' : 'Update Subscription'}
                        </button>
                    </div>
                </form>
            </Modal>

            {/* Delete Confirmation Modal */}
            <Modal
                isOpen={isDeleteModalOpen}
                onRequestClose={closeDeleteModal}
                contentLabel="Delete Confirmation Modal"
                className="delete-confirmation-modal"
                overlayClassName="add-shelter-overlay"
                ariaHideApp={false}
            >
                <div className="delete-modal-header">
                    <button className="close-btn" onClick={closeDeleteModal}>
                        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <path d="M18 6L6 18M6 6L18 18" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                        </svg>
                    </button>
                </div>

                <div className="delete-modal-content">
                    <div className="delete-warning-icon">
                        <svg width="64" height="64" viewBox="0 0 64 64" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <circle cx="32" cy="32" r="32" fill="#f44336"/>
                            <path d="M32 20V36" stroke="white" strokeWidth="4" strokeLinecap="round"/>
                            <circle cx="32" cy="44" r="2" fill="white"/>
                        </svg>
                    </div>
                    
                    <h2 className="delete-modal-title">Delete Confirmation</h2>
                    
                    <p className="delete-modal-message">
                        Are you sure you want to delete this subscription
                    </p>
                    
                    <div className="delete-modal-actions">
                        <button 
                            className="delete-confirm-btn" 
                            onClick={confirmDelete}
                            disabled={deleteLoader}
                        >
                            {deleteLoader ? 'Deleting...' : 'Delete'}
                        </button>
                        <button 
                            className="delete-cancel-btn" 
                            onClick={closeDeleteModal}
                            disabled={deleteLoader}
                        >
                            Not now
                        </button>
                    </div>
                </div>
            </Modal>

            {/* View Subscription Details Modal */}
            <Modal
                isOpen={isViewModalOpen}
                onRequestClose={closeViewModal}
                contentLabel="Subscription Details"
                className="add-shelter-modal subscription-details-modal"
                overlayClassName="add-shelter-overlay"
                ariaHideApp={false}
            >
                <div className="modal-header">
                    <div>
                        <h2>Subscription Details</h2>
                    </div>
                    <button className="close-btn" onClick={closeViewModal} aria-label="Close">
                        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <path d="M18 6L6 18M6 6L18 18" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                        </svg>
                    </button>
                </div>

                {viewLoader ? (
                    <div className="subscription-details-body" style={{ padding: '40px', textAlign: 'center' }}>
                        <p>Loading subscription details...</p>
                    </div>
                ) : viewSubscriptionData ? (
                    <div className="subscription-details-body">
                        <div className="subscription-detail-user-row">
                            <div className="subscription-detail-user-info">
                                <h3 className="subscription-detail-name">{viewSubscriptionData.name}</h3>
                                <p className="subscription-detail-email">{viewSubscriptionData.email}</p>
                            </div>
                            <div className="subscription-detail-badges">
                                <span className={`plan-tag ${viewSubscriptionData.planKey === 'premium' ? 'premium' : 'free'}`}>
                                    {viewSubscriptionData.planKey === 'premium' ? 'Premium plan' : viewSubscriptionData.plan}
                                </span>
                                <span className={`status-badge ${viewSubscriptionData.status === 'Active' ? 'active' : 'inactive'}`}>
                                    {viewSubscriptionData.status}
                                </span>
                            </div>
                        </div>

                        <div className="subscription-detail-cards">
                            <div className="subscription-detail-card">
                                <span className="subscription-detail-card-label">Amount:</span>
                                <span className="subscription-detail-card-value">{viewSubscriptionData.amount}</span>
                            </div>
                            <div className="subscription-detail-card">
                                <span className="subscription-detail-card-label">Payment Method:</span>
                                <span className="subscription-detail-card-value">{viewSubscriptionData.paymentMethod}</span>
                            </div>
                            </div>
                            <div className="subscription-detail-cards subscription-detail-cards-start-end">
                            <div className="subscription-detail-card">
                                <span className="subscription-detail-card-label">Start Date:</span>
                                <span className="subscription-detail-card-value">{viewSubscriptionData.startDate}</span>
                            </div>
                            <div className="subscription-detail-card">
                                <span className="subscription-detail-card-label">End Date:</span>
                                <span className="subscription-detail-card-value">{viewSubscriptionData.endDate}</span>
                            </div>
                            <div className="subscription-detail-card">
                                <span className="subscription-detail-card-label">Next Billing:</span>
                                <span className="subscription-detail-card-value">{viewSubscriptionData.nextBilling}</span>
                            </div>
                            </div>
                            <div className="subscription-detail-cards">
                            <div className="subscription-detail-card">
                                <span className="subscription-detail-card-label">Transaction ID:</span>
                                <span className="subscription-detail-card-value">{viewSubscriptionData.transactionId}</span>
                            </div>
                            <div className="subscription-detail-card">
                                <span className="subscription-detail-card-label">Renewal Status:</span>
                                <span className="subscription-detail-card-value subscription-detail-renewal">
                                    {viewSubscriptionData.renewalStatus}
                                </span>
                            </div>
                            <div className="subscription-detail-button-cancel">
                                <button className="cancel-subscription-btn">Cancel Subscription</button>
                            </div>
                        </div>
                    </div>
                ) : (
                    <div className="subscription-details-body" style={{ padding: '40px', textAlign: 'center' }}>
                        <p>No data available</p>
                    </div>
                )}
            </Modal>
        </div>
    );
};

export default Subscription;
